"use client"
import { useState, useEffect } from "react"
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  FormFeedback,
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Row,
  Col,
  Spinner,
  FormText,
} from "reactstrap"
import { Btn } from "../../../AbstractElements"
import { PersonService } from "../../../Service/person-service"
import type { PersonWithAccount, CreatePersonRequest } from "./types"
import { toast } from "react-toastify"

interface PersonModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  initialData: PersonWithAccount | null
}

export default function PersonModal({ isOpen, onClose, onSuccess, initialData }: PersonModalProps) {
  const [loading, setLoading] = useState(false)
  const [form, setForm] = useState<CreatePersonRequest>({
    first_name: "",
    middle_name: "",
    last_name: "",
    email: "",
    phone: "",
    is_admin: false,
    is_verifier: false,
    is_staff: false,
    is_manager: false, 
  })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [touched, setTouched] = useState<Record<string, boolean>>({})

  useEffect(() => {
    // No need to fetch ID document types as that section is removed
  }, [])

  useEffect(() => {
    if (initialData) {
      setForm({
        first_name: initialData.first_name,
        middle_name: initialData.middle_name || "",
        last_name: initialData.last_name,
        email: initialData.email || "",
        phone: initialData.phone || "",
        // Access account roles via initialData.account with optional chaining
        is_admin: initialData.account?.is_admin || false,
        is_verifier: initialData.account?.is_verifier || false,
        is_staff: initialData.account?.is_staff || false,
        is_manager: initialData.account?.is_manager || false, 
      })
    } else {
      setForm({
        first_name: "",
        middle_name: "",
        last_name: "",
        email: "",
        phone: "",
        is_admin: false,
        is_verifier: false,
        is_staff: false,
        is_manager: false,
      })
    }
    setErrors({})
    setTouched({})
  }, [initialData, isOpen])

  const handleInputChange = (field: keyof CreatePersonRequest, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }))
    }
  }

  const handleBlur = (field: keyof CreatePersonRequest) => {
    setTouched((prev) => ({ ...prev, [field]: true }))
    validateField(field)
  }

  const validatePhone = (phone: string): boolean => {
    if (!phone) return true // Optional field
    // Remove all non-digit characters
    const cleanPhone = phone.replace(/\D/g, "")
    // Check if it's exactly 10 digits
    if (cleanPhone.length !== 10) {
      return false
    }
    // Ensure it's only digits
    const phoneRegex = /^\d{10}$/
    return phoneRegex.test(cleanPhone)
  }

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const validateField = (field: keyof CreatePersonRequest) => {
    const newErrors = { ...errors }
    switch (field) {
      case "first_name":
        if (!form.first_name.trim()) {
          newErrors.first_name = "First name is required"
        } else if (form.first_name.trim().length < 2) {
          newErrors.first_name = "First name must be at least 2 characters"
        } else if (!/^[a-zA-Z\s'-]+$/.test(form.first_name)) {
          newErrors.first_name = "First name can only contain letters, spaces, hyphens, and apostrophes"
        } else {
          delete newErrors.first_name
        }
        break
      case "last_name":
        if (!form.last_name.trim()) {
          newErrors.last_name = "Last name is required"
        } else if (form.last_name.trim().length < 2) {
          newErrors.last_name = "Last name must be at least 2 characters"
        } else if (!/^[a-zA-Z\s'-]+$/.test(form.last_name)) {
          newErrors.last_name = "Last name can only contain letters, spaces, hyphens, and apostrophes"
        } else {
          delete newErrors.last_name
        }
        break
      case "email":
        if (!form.email.trim()) {
          newErrors.email = "Email is required"
        } else if (!validateEmail(form.email)) {
          newErrors.email = "Please enter a valid email address"
        } else {
          delete newErrors.email
        }
        break
      case "phone":
        if (form.phone && !validatePhone(form.phone)) {
          newErrors.phone = "Please enter a valid phone number (exactly 10 digits)"
        } else {
          delete newErrors.phone
        }
        break
    }
    setErrors(newErrors)
  }

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}
    // Required fields validation
    if (!form.first_name.trim()) {
      newErrors.first_name = "First name is required"
    } else if (form.first_name.trim().length < 2) {
      newErrors.first_name = "First name must be at least 2 characters"
    } else if (!/^[a-zA-Z\s'-]+$/.test(form.first_name)) {
      newErrors.first_name = "First name can only contain letters, spaces, hyphens, and apostrophes"
    }
    if (!form.last_name.trim()) {
      newErrors.last_name = "Last name is required"
    } else if (form.last_name.trim().length < 2) {
      newErrors.last_name = "Last name must be at least 2 characters"
    } else if (!/^[a-zA-Z\s'-]+$/.test(form.last_name)) {
      newErrors.last_name = "Last name can only contain letters, spaces, hyphens, and apostrophes"
    }
    if (!form.email.trim()) {
      newErrors.email = "Email is required"
    } else if (!validateEmail(form.email)) {
      newErrors.email = "Please enter a valid email address"
    }
    // Phone validation (optional but must be valid if provided)
    if (form.phone && !validatePhone(form.phone)) {
      newErrors.phone = "Please enter a valid phone number (exactly 10 digits)"
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validateForm()) return

    try {
      setLoading(true)
      const payload = {
        first_name: form.first_name,
        middle_name: form.middle_name || undefined,
        last_name: form.last_name,
        email: form.email,
        phone: form.phone || undefined,
        is_admin: form.is_admin,
        is_verifier: form.is_verifier,
        is_staff: form.is_staff,
        is_manager: form.is_manager, 
      }

      if (initialData) {
        // Update existing person
        await PersonService.update(initialData.id, payload)
        toast.success("User updated successfully")
      } else {
        // Create new person
        await PersonService.create(payload)
        toast.success("User created successfully. Verification email sent.")
      }
      onSuccess()
      onClose()
    } catch (error: any) {
      console.error("Failed to save user:", error)
      if (error.response?.data) {
        const backendErrors = error.response.data
        let hasFieldErrors = false
        const newErrors: Record<string, string> = {}

        // Check for field-specific errors (e.g., { email: ["Email already exists"] })
        for (const key in backendErrors) {
          // Ensure the key exists in our form state and the value is an array with messages
          if (form.hasOwnProperty(key) && Array.isArray(backendErrors[key]) && backendErrors[key].length > 0) {
            newErrors[key] = backendErrors[key][0] // Take the first error message for the field
            hasFieldErrors = true
          }
        }

        if (hasFieldErrors) {
          setErrors((prev) => ({ ...prev, ...newErrors }))
          //toast.error("Please correct the highlighted errors.")
        } else if (backendErrors.error) {
          // Handle a general 'error' field if present
          toast.error(backendErrors.error)
        } else {
          // Fallback for any other unexpected backend error structure
          toast.error("Failed to save user. Please try again.")
        }
      } else {
        // Handle cases where there's no response data (e.g., network error)
        toast.error("Failed to save user. Please check your network connection and try again.")
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg" className="modal-dialog-centered">
      <ModalHeader toggle={onClose} className="bg-primary text-white">
        <i className="fa fa-user me-2"></i>
        {initialData ? "Edit Person" : "Add New Person"}
      </ModalHeader>
      <ModalBody className="p-4">
        <Form>
          {/* Personal Information */}
          <Card className="mb-4 shadow-sm">
            <CardHeader className="bg-light">
              <CardTitle tag="h5" className="mb-0 text-primary">
                <i className="fa fa-user-circle me-2"></i>
                Personal Information
              </CardTitle>
              <small className="text-muted">Basic personal details and contact information</small>
            </CardHeader>
            <CardBody>
              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label for="first_name" className="fw-bold">
                      First Name <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="text"
                      id="first_name"
                      value={form.first_name}
                      onChange={(e) => handleInputChange("first_name", e.target.value)}
                      onBlur={() => handleBlur("first_name")}
                      invalid={touched.first_name && !!errors.first_name}
                      placeholder="Enter first name"
                      className="form-control-lg"
                    />
                    {touched.first_name && errors.first_name && <FormFeedback>{errors.first_name}</FormFeedback>}
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label for="middle_name" className="fw-bold">
                      Middle Name
                    </Label>
                    <Input
                      type="text"
                      id="middle_name"
                      value={form.middle_name}
                      onChange={(e) => handleInputChange("middle_name", e.target.value)}
                      placeholder="Enter middle name (optional)"
                      className="form-control-lg"
                    />
                  </FormGroup>
                </Col>
              </Row>
              <FormGroup>
                <Label for="last_name" className="fw-bold">
                  Last Name <span className="text-danger">*</span>
                </Label>
                <Input
                  type="text"
                  id="last_name"
                  value={form.last_name}
                  onChange={(e) => handleInputChange("last_name", e.target.value)}
                  onBlur={() => handleBlur("last_name")}
                  invalid={touched.last_name && !!errors.last_name}
                  placeholder="Enter last name"
                  className="form-control-lg"
                />
                {touched.last_name && errors.last_name && <FormFeedback>{errors.last_name}</FormFeedback>}
              </FormGroup>
              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label for="email" className="fw-bold">
                      <i className="fa fa-envelope me-1"></i>
                      Email <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="email"
                      id="email"
                      value={form.email}
                      onChange={(e) => handleInputChange("email", e.target.value)}
                      onBlur={() => handleBlur("email")}
                      invalid={touched.email && !!errors.email}
                      placeholder="Enter email address"
                      className="form-control-lg"
                    />
                    {touched.email && errors.email && <FormFeedback>{errors.email}</FormFeedback>}
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label for="phone" className="fw-bold">
                      <i className="fa fa-phone me-1"></i>
                      Phone
                    </Label>
                    <Input
                      type="tel"
                      id="phone"
                      value={form.phone}
                      onChange={(e) => handleInputChange("phone", e.target.value)}
                      onBlur={() => handleBlur("phone")}
                      invalid={touched.phone && !!errors.phone}
                      placeholder="07XXXXXXXX"
                      className="form-control-lg"
                    />
                    {touched.phone && errors.phone && <FormFeedback>{errors.phone}</FormFeedback>}
                    {!errors.phone && form.phone && (
                      <FormText color="muted">
                        <i className="fa fa-info-circle me-1"></i>
                        Phone number should be exactly 10 digits
                      </FormText>
                    )}
                  </FormGroup>
                </Col>
              </Row>
            </CardBody>
          </Card>

          {/* Account Roles */}
          <Card className="mb-4 shadow-sm">
            <CardHeader className="bg-light">
              <CardTitle tag="h5" className="mb-0 text-primary">
                <i className="fa fa-lock me-2"></i>
                Dashboard Access
              </CardTitle>
              <small className="text-muted">dashboard access setting</small>
            </CardHeader>
            <CardBody>
              <Row>
                <Col md="6">
                  <FormGroup check className="mb-3">
                    <Input
                      type="checkbox"
                      id="is_staff"
                      checked={form.is_staff}
                      onChange={(e) => handleInputChange("is_staff", e.target.checked)}
                    />
                    <Label check for="is_staff" className="fw-bold ms-2">
                      Is Staff
                    </Label>
                    <FormText color="muted" className="d-block ms-4">
                      Grants access to the staff dashboard.
                    </FormText>
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup check className="mb-3">
                    <Input
                      type="checkbox"
                      id="is_admin"
                      checked={form.is_admin}
                      onChange={(e) => handleInputChange("is_admin", e.target.checked)}
                    />
                    <Label check for="is_admin" className="fw-bold ms-2">
                      Is Admin
                    </Label>
                    <FormText color="muted" className="d-block ms-4">
                      Grants access to admin dashboard.
                    </FormText>
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col md="6">
                  <FormGroup check className="mb-3">
                    <Input
                      type="checkbox"
                      id="is_verifier"
                      checked={form.is_verifier}
                      onChange={(e) => handleInputChange("is_verifier", e.target.checked)}
                    />
                    <Label check for="is_verifier" className="fw-bold ms-2">
                      Is Verifier
                    </Label>
                    <FormText color="muted" className="d-block ms-4">
                      Grants access to verification dashboard.
                    </FormText>
                  </FormGroup>
                </Col>

                <Col md="6">
                  <FormGroup check className="mb-3">
                    <Input
                      type="checkbox"
                      id="is_manager"
                      checked={form.is_manager}
                      onChange={(e) => handleInputChange("is_manager", e.target.checked)}
                    />
                    <Label check for="is_manager" className="fw-bold ms-2">
                      Is Manager
                    </Label>
                    <FormText color="muted" className="d-block ms-4">
                      Grants access to manager dashboard.
                    </FormText>
                  </FormGroup>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Form>
      </ModalBody>
      <ModalFooter className="bg-light">
        <Btn color="secondary" onClick={onClose} disabled={loading} className="me-2">
          <i className="fa fa-times me-1"></i>
          Cancel
        </Btn>
        <Btn color="primary" onClick={handleSubmit} disabled={loading}>
          {loading && <Spinner size="sm" className="me-2" />}
          <i className={`fa ${loading ? "fa-spinner fa-spin" : initialData ? "fa-save" : "fa-plus"} me-1`}></i>
          {initialData ? "Update Person" : "Create Person"}
        </Btn>
      </ModalFooter>
    </Modal>
  )
}
